import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

// Hook to fetch CV data from Contentful
const useCVData = () => {
  const [cvData, setCvData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCVData = async () => {
      try {
        // Try to fetch from Contentful
        const spaceId = process.env.REACT_APP_CONTENTFUL_SPACE_ID;
        const accessToken = process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN;
        
        if (spaceId && accessToken) {
          const response = await fetch(
            `https://cdn.contentful.com/spaces/${spaceId}/environments/master/entries?content_type=cvPage&limit=1`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.items && data.items.length > 0) {
              const entry = data.items[0];
              setCvData(entry.fields);
              setIsLoading(false);
              return;
            }
          }
        }
        
        // Fallback - no data yet
        setCvData(null);
        setIsLoading(false);
      } catch (error) {
        console.warn('Failed to fetch CV data:', error);
        setCvData(null);
        setIsLoading(false);
      }
    };

    fetchCVData();
  }, []);

  return { cvData, isLoading };
};

// Component to render a CV section from JSON
const CVSection = ({ title, data, type = 'list' }) => {
  if (!data || (Array.isArray(data) && data.length === 0)) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mb-12"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-primary mb-6 pb-2 border-b-2 border-brand-secondary">
        {title}
      </h2>
      
      {type === 'paragraph' && (
        <p className="text-slate-600 leading-relaxed">{data}</p>
      )}
      
      {type === 'list' && (
        <ul className="space-y-2">
          {data.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="w-2 h-2 bg-brand-secondary rounded-full mt-2 flex-shrink-0" />
              <span className="text-slate-600">{typeof item === 'string' ? item : item.text}</span>
            </li>
          ))}
        </ul>
      )}
      
      {type === 'experience' && (
        <div className="space-y-8">
          {data.map((exp, idx) => (
            <div key={idx} className="border-l-2 border-brand-accent pl-6 pb-4">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <h3 className="font-heading text-xl font-bold text-brand-primary">{exp.title}</h3>
                <span className="text-brand-secondary font-medium text-sm bg-brand-secondary/10 px-3 py-1 rounded-full">
                  {exp.dates}
                </span>
              </div>
              <p className="text-brand-secondary font-medium mb-2">{exp.organization}</p>
              {exp.location && <p className="text-slate-500 text-sm mb-3">{exp.location}</p>}
              {exp.description && <p className="text-slate-600 mb-3">{exp.description}</p>}
              {exp.highlights && exp.highlights.length > 0 && (
                <ul className="space-y-1">
                  {exp.highlights.map((h, hIdx) => (
                    <li key={hIdx} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 bg-brand-secondary rounded-full mt-2 flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
      
      {type === 'education' && (
        <div className="space-y-6">
          {data.map((edu, idx) => (
            <div key={idx} className="bg-brand-accent rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                <h3 className="font-heading text-lg font-bold text-brand-primary">{edu.degree}</h3>
                <span className="text-slate-500 text-sm">{edu.date}</span>
              </div>
              <p className="text-brand-secondary">{edu.institution}</p>
              {edu.details && <p className="text-slate-600 text-sm mt-2">{edu.details}</p>}
            </div>
          ))}
        </div>
      )}
      
      {type === 'skills' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data.map((skill, idx) => (
            <div key={idx} className="bg-brand-accent rounded-xl p-5">
              <h3 className="font-heading font-bold text-brand-primary mb-2">{skill.category}</h3>
              <p className="text-slate-600 text-sm">{skill.items.join(', ')}</p>
            </div>
          ))}
        </div>
      )}
      
      {type === 'awards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.map((award, idx) => (
            <div key={idx} className="flex items-start gap-3 p-4 bg-white border border-slate-100 rounded-xl">
              <div className="w-10 h-10 bg-brand-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-brand-secondary font-bold text-xs">{award.year?.toString().slice(-2) || '★'}</span>
              </div>
              <div>
                <p className="font-medium text-brand-primary">{award.title}</p>
                {award.organization && <p className="text-slate-500 text-sm">{award.organization}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {type === 'certifications' && (
        <div className="space-y-4">
          {data.map((cert, idx) => (
            <div key={idx} className="border-l-2 border-brand-secondary pl-4 py-2">
              <h3 className="font-semibold text-brand-primary">{cert.name}</h3>
              <p className="text-brand-secondary text-sm">{cert.organization}</p>
              <p className="text-slate-500 text-sm">{cert.dates}</p>
            </div>
          ))}
        </div>
      )}
      
      {type === 'publications' && (
        <div className="space-y-6">
          {data.map((pub, idx) => (
            <div key={idx} className="border-b border-slate-100 pb-4">
              <h3 className="font-semibold text-brand-primary mb-1">{pub.title}</h3>
              {pub.authors && <p className="text-slate-600 text-sm mb-1">{pub.authors}</p>}
              <p className="text-slate-500 text-sm">
                {pub.venue} {pub.date && `• ${pub.date}`}
              </p>
              {pub.link && (
                <a 
                  href={pub.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-secondary text-sm hover:underline mt-2"
                >
                  View Publication <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
      
      {type === 'memberships' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.map((mem, idx) => (
            <div key={idx} className="bg-brand-accent rounded-xl p-5">
              <h3 className="font-semibold text-brand-primary mb-1">{mem.organization}</h3>
              {mem.role && <p className="text-brand-secondary text-sm mb-1">{mem.role}</p>}
              {mem.dates && <p className="text-slate-500 text-sm">{mem.dates}</p>}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default function CV() {
  const navigate = useNavigate();
  const { cvData, isLoading } = useCVData();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-brand-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading CV...</p>
        </div>
      </div>
    );
  }

  // Empty state when no Contentful data
  if (!cvData) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <section className="py-12 md:py-20 bg-brand-primary text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-white/70 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                Curriculum Vitae
              </h1>
              <p className="text-white/80 text-xl">
                Coming Soon
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <div className="bg-brand-accent rounded-2xl p-8 md:p-12">
              <div className="w-16 h-16 bg-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Download className="w-8 h-8 text-brand-secondary" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-brand-primary mb-4">
                CV Content Coming Soon
              </h2>
              <p className="text-slate-600 mb-6">
                My full curriculum vitae will be available here shortly. In the meantime, feel free to get in touch to request a copy.
              </p>
              <Button
                asChild
                className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90"
              >
                <Link to="/contact">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Me
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="cv-page" className="min-h-screen bg-white">
      {/* Header */}
      <section className="py-12 md:py-20 bg-brand-primary text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-white/70 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              {cvData.name || 'Gaurav Shekhar, PhD'}
            </h1>
            {cvData.title && (
              <p className="text-white/80 text-xl mb-6">{cvData.title}</p>
            )}
            
            {/* Contact Info */}
            <div className="flex flex-wrap gap-6 text-sm">
              {cvData.location && (
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {cvData.location}
                </span>
              )}
              {cvData.email && (
                <a href={`mailto:${cvData.email}`} className="flex items-center gap-2 hover:text-brand-secondary">
                  <Mail className="w-4 h-4" />
                  {cvData.email}
                </a>
              )}
              {cvData.phone && (
                <span className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {cvData.phone}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CV Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Summary */}
          {cvData.summary && (
            <CVSection title="Summary" data={cvData.summary} type="paragraph" />
          )}
          
          {/* Experience */}
          {cvData.experience && (
            <CVSection title="Work Experience" data={cvData.experience} type="experience" />
          )}
          
          {/* Education */}
          {cvData.education && (
            <CVSection title="Education" data={cvData.education} type="education" />
          )}
          
          {/* Skills */}
          {cvData.skills && (
            <CVSection title="Skills" data={cvData.skills} type="skills" />
          )}
          
          {/* Awards */}
          {cvData.awards && (
            <CVSection title="Awards & Recognition" data={cvData.awards} type="awards" />
          )}
          
          {/* Certifications */}
          {cvData.certifications && (
            <CVSection title="Certifications" data={cvData.certifications} type="certifications" />
          )}
          
          {/* Publications */}
          {cvData.publications && (
            <CVSection title="Publications" data={cvData.publications} type="publications" />
          )}
          
          {/* Memberships */}
          {cvData.memberships && (
            <CVSection title="Professional Memberships" data={cvData.memberships} type="memberships" />
          )}
          
          {/* Appointments */}
          {cvData.appointments && (
            <CVSection title="Appointments" data={cvData.appointments} type="list" />
          )}
          
          {/* Languages */}
          {cvData.languages && (
            <CVSection title="Languages" data={cvData.languages} type="list" />
          )}
          
          {/* Any additional custom sections from JSON */}
          {cvData.customSections && cvData.customSections.map((section, idx) => (
            <CVSection 
              key={idx} 
              title={section.title} 
              data={section.data} 
              type={section.type || 'list'} 
            />
          ))}
        </div>
      </section>
    </div>
  );
}
